import { HttpClient } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';

import { BehaviorSubject, interval, Observable, of, startWith, switchMap, takeWhile } from 'rxjs';

import {
    ApiDataType,
    EnvironmentInterface,
    GlobalsInterface,
    NotificationsDTO,
    UserNotificationInterface
} from '../../types';
import { GuardService } from '../guard';
import { ClientUserService } from '../users';

@Injectable({
    providedIn: 'root'
})
export class NotificationsService {
    /* eslint-disable */
    clientUserService = inject(ClientUserService);
    proUserService = inject(GuardService);
    lastNotificationId: number | null = null;
    viewedUserNotifications: boolean = false;
    notifications: UserNotificationInterface[] = [];
    enabledSubscription: boolean = false;
    subscription: any;
    lastId = 0;
    notificationInterval = 10000;
    private environment: EnvironmentInterface;
    private globals: GlobalsInterface;
    private notificationCountSubject = new BehaviorSubject<number>(0);
    notificationCount$ = this.notificationCountSubject.asObservable();

    private notificationsSubject = new BehaviorSubject<UserNotificationInterface[]>([]);
    notifications$ = this.notificationsSubject.asObservable();

    private lastNotificationIdSubject = new BehaviorSubject<number | null>(null);
    lastNotificationId$ = this.lastNotificationIdSubject.asObservable();

    private lastIdSubject = new BehaviorSubject<number>(0);
    lastId$ = this.lastIdSubject.asObservable();

    private viewedUserNotificationsSubject = new BehaviorSubject<boolean>(false);
    viewedUserNotifications$ = this.viewedUserNotificationsSubject.asObservable();

    constructor(
        private http: HttpClient,
        @Inject('globals') globals: GlobalsInterface,
        @Inject('environment') environment: EnvironmentInterface
    ) {
        this.globals = globals;
        this.environment = environment;
        this.notificationInterval = globals.notificationPollInterval || 10000;
    }

    updateNotificationCount(newCount: number) {
        this.notificationCountSubject.next(newCount);
    }

    clearNotifications() {
        this.enabledSubscription = false;

        this.setLastNotificationId(null);
        this.updateNotificationCount(0);
        this.setNotifications([]);
        this.setLastId(0);
        this.setViewedUserNotifications(false);
    }

    getHasEnabledSubscription() {
        return this.enabledSubscription;
    }

    setHasEnabledSubscription(value: boolean) {
        this.enabledSubscription = value;
    }

    getNotifications() {
        return this.notificationsSubject.value;
    }

    setNotifications(notifications: UserNotificationInterface[]) {
        this.notificationsSubject.next(notifications);
    }

    addNotifications(notifications: UserNotificationInterface[]) {
        const newNotifications = [...notifications, ...this.notifications];
        this.setNotifications(newNotifications);
    }

    removeNotification(id: number): Observable<ApiDataType> {
        return this.http.get<ApiDataType>(`${this.environment.apiUrl}/notifications/read/${id}`);
    }

    getUserNotifications(): Observable<any[]> {
        console.log(this.notificationsSubject.value);
        return of(this.notificationsSubject.value);
    }

    updateUserNotifications(notifications: UserNotificationInterface[]) {
        // this.notifications = notifications;
        this.setNotifications(notifications);
    }

    hasViewedUserNotifications() {
        // this.viewedUserNotifications = true;
        this.setViewedUserNotifications(true);
        return true;
    }

    getViewedUserNotifications() {
        return this.viewedUserNotificationsSubject.value;
    }

    setViewedUserNotifications(value: boolean) {
        this.viewedUserNotificationsSubject.next(value);
    }

    getNewNotifications(id: number): Observable<NotificationsDTO> {
        return this.http.get<NotificationsDTO>(`${this.environment.apiUrl}/notifications/after/${id}`);
    }

    getLastNotificationId() {
        return this.lastNotificationIdSubject.value;
    }

    setLastNotificationId(id: number | null) {
        this.lastNotificationIdSubject.next(id);
    }

    getLasId() {
        return this.lastIdSubject.value;
    }

    setLastId(id: number) {
        this.lastIdSubject.next(id);
    }

    getUserLastNotificationId(userNotifications: Array<UserNotificationInterface> = []) {
        const lastNotification = userNotifications.reduce(function (a: any, b: any) {
            if (a.id > b.id) {
                return a;
            } else if (a.id < b.id) {
                return b;
            } else if (a.id === b.id) {
                return a;
            } else {
                return 0;
            }
        });

        return lastNotification['id'] || 0;
    }

    startNotificationSubscription() {
        const enabled = this.getHasEnabledSubscription();

        if (!enabled) {
            this.setHasEnabledSubscription(true);

            this.subscription = interval(this.notificationInterval)
                .pipe(
                    takeWhile(() => {
                        if (this.globals.appType === 'client') {
                            return this.clientUserService.userExists() === true;
                        } else {
                            return this.proUserService.userExists() === true;
                        }
                    }),
                    startWith(0),
                    switchMap(() => this.getNewNotifications(this.lastIdSubject.value))
                )
                .subscribe(res => {
                    // console.log('polled for notifications: ' + new Date());
                    // console.log(res);

                    if (res.success === true) {
                        if (res.data && res.data.length > 0) {
                            if (!this.notificationCountSubject.value) {
                                this.updateNotificationCount(0);
                            }

                            this.updateNotificationCount(this.notificationCountSubject.value + res.data.length);

                            const lastId = this.getUserLastNotificationId(res.data);
                            this.lastId = lastId;
                            this.setLastNotificationId(lastId);

                            this.addNotifications(res.data);

                            // console.log(this.lastId);
                        }
                    }
                });
        }
    }

    stopNotificationSubscription() {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.setHasEnabledSubscription(false);
        }
    }
}
